/**
 * useEmailPerformanceData
 *
 * DASH-EMAIL-P1-IMPL-008: Performance's five-stage funnel (Delivered/
 * Landing Page Views/Quote Starts/Quote Completes/High Intent Actions) is
 * now real — the SAME authoritative EDT `/summary` and LPE `/lpe-funnel`
 * contracts Overview already consumes (useEmailDashboardData.js), reused
 * directly rather than reinterpreted. No metric is redefined here; this
 * hook only decides WHICH existing calls to make and how to shape their
 * results for this view — it duplicates no backend semantics.
 *
 * Retires the mock-only shell phase (emailPerformanceMockAdapter.js's
 * BROADCAST_ROWS_BASE-derived summary/funnel/broadcasts). That file is
 * left in place, unconsumed, as documented follow-up debt (see this
 * checkpoint's own completion report) rather than deleted — its
 * `PROGRAM_HIERARCHY` export remains actively used elsewhere (Overview's
 * own Program/Campaign Advanced-row controls).
 *
 * (Historical note, DASH-EMAIL-P1-IMPL-008/CHAR-007): before DASH-EMAIL-P1-
 * IMPL-032, this hook accepted ONLY `period`/`customRange` -- applying
 * Environment only to Delivered while LPE stayed account-level would have
 * presented the five-stage funnel as one coherently-filtered cohort when
 * it was not. #350/#351 removed that asymmetry: EDT `/summary` and LPE
 * `/lpe-funnel` now each independently resolve every governed dimension
 * through their own authoritative source, so both real fetches below can
 * be scoped consistently by the SAME seven filters Overview already uses
 * (see useEmailDashboardData.js's own header comment for the exact
 * per-domain parameter mapping, including why `environment` maps to a
 * DIFFERENT parameter name on each domain -- this hook mirrors that
 * mapping exactly, never conflating the two authorities).
 */

import { useState, useEffect, useCallback } from "react";
import { getEmailDeliveryDashboardSummary } from "../lib/emailDeliveryDashboardApi";
import { getEmailDeliveryLpeFunnelSummary } from "../lib/emailDeliveryLpeFunnelApi";

const SUMMARY_KEYS = ["delivered", "landing_page_views", "quote_starts", "quote_completes", "high_intent_actions"];
const FUNNEL_LABELS = {
  delivered: "Delivered",
  landing_page_views: "Landing Page Views",
  quote_starts: "Quote Starts",
  quote_completes: "Quote Completes",
  high_intent_actions: "High Intent Actions",
};
// DASH-EMAIL-P1-IMPL-008: the same LPE response-key vocabulary
// useEmailDashboardData.js's fetchRealLpeFunnelStages already uses — one
// canonical mapping of this contract, not a second interpretation of it.
const LPE_KEYS = ["landing_page_views", "quote_starts", "quote_completes", "high_intent_actions"];
const MISSING_DATA_REASON = "This metric could not be retrieved from the live data source.";

async function fetchRealDelivered(
  period,
  customRange,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
  observedGhlEnvironment
) {
  try {
    const summary = await getEmailDeliveryDashboardSummary({
      period,
      startDate: period === "custom" ? customRange?.startDate : undefined,
      endDate: period === "custom" ? customRange?.endDate : undefined,
      broadcastId: governedBroadcastId || undefined,
      audienceId: governedAudienceId || undefined,
      programId: governedProgramId || undefined,
      campaignId: governedCampaignId || undefined,
      state: governedState || undefined,
      observedGhlEnvironment: observedGhlEnvironment || undefined,
    });
    const delivered = summary?.metrics?.delivered;
    return typeof delivered === "number" ? delivered : null;
  } catch (err) {
    // Defensive, independent of the LPE fetch below — never blocks the
    // rest of this view from rendering, and never falls back to a
    // fabricated Delivered number; the stage simply stays unavailable.
    return null;
  }
}

async function fetchRealLpeStages(
  period,
  customRange,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
  lpeEnvironment
) {
  try {
    const response = await getEmailDeliveryLpeFunnelSummary({
      period,
      startDate: period === "custom" ? customRange?.startDate : undefined,
      endDate: period === "custom" ? customRange?.endDate : undefined,
      broadcastId: governedBroadcastId || undefined,
      audienceId: governedAudienceId || undefined,
      programId: governedProgramId || undefined,
      campaignId: governedCampaignId || undefined,
      state: governedState || undefined,
      environment: lpeEnvironment || undefined,
    });
    const values = {};
    for (const key of LPE_KEYS) {
      const stage = response?.[key];
      values[key] = typeof stage?.value === "number" ? stage.value : null;
    }
    return { values, attributionCoverage: response?.attribution_coverage ?? null };
  } catch (err) {
    // Defensive, independent of the Delivered fetch above — never
    // fabricates a value for any of the four stages; each stays
    // unavailable. attributionCoverage stays null (unknown).
    const values = {};
    for (const key of LPE_KEYS) values[key] = null;
    return { values, attributionCoverage: null };
  }
}

// Builds both the KPICard-shaped `summary` (value/status/reason) and the
// EmailFunnelCard-shaped `funnel` (label/value/status) from the SAME
// underlying real values, in the fixed Delivered -> LPE stage order — same
// "one set of numbers, two display shapes" relationship
// emailPerformanceMockAdapter.js's retired buildSummary/buildFunnel pair
// used, just fed by real fetches instead of a fixture.
function buildSummaryAndFunnel(deliveredValue, lpeValues, attributionCoverage) {
  const raw = { delivered: deliveredValue, ...lpeValues };
  const summary = {};
  const funnel = [];
  for (const key of SUMMARY_KEYS) {
    const value = raw[key];
    const status = value !== null ? "exact" : "missing_data";
    summary[key] = { value, status, reason: value === null ? MISSING_DATA_REASON : undefined };
    funnel.push({ label: FUNNEL_LABELS[key], value, status });
  }
  return { summary, funnel, attributionCoverage };
}

const fetchSource = async ({
  period,
  customRange,
  governedBroadcastId,
  governedAudienceId,
  governedProgramId,
  governedCampaignId,
  governedState,
  observedGhlEnvironment,
  lpeEnvironment,
}) => {
  const [deliveredValue, lpeResult] = await Promise.all([
    fetchRealDelivered(
      period,
      customRange,
      governedBroadcastId,
      governedAudienceId,
      governedProgramId,
      governedCampaignId,
      governedState,
      observedGhlEnvironment
    ),
    fetchRealLpeStages(
      period,
      customRange,
      governedBroadcastId,
      governedAudienceId,
      governedProgramId,
      governedCampaignId,
      governedState,
      lpeEnvironment
    ),
  ]);
  return buildSummaryAndFunnel(deliveredValue, lpeResult.values, lpeResult.attributionCoverage);
};

export function useEmailPerformanceData(options = {}) {
  const {
    period = "mtd",
    customRange = null,
    // DASH-EMAIL-P1-IMPL-032: the same seven governed filters Overview
    // uses -- see useEmailDashboardData.js's own header comment for the
    // exact per-domain parameter mapping.
    environment = null,
    governedBroadcastId = null,
    governedAudienceId = null,
    governedProgramId = null,
    governedCampaignId = null,
    governedState = null,
    enabled = true,
  } = options;

  // Initialize as loading (when enabled) so the view renders its loading
  // skeleton on first paint instead of briefly flashing the empty state —
  // same reasoning as useEmailDashboardData.
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const observedGhlEnvironment = environment && environment !== "all" ? environment : null;
  const lpeEnvironment = environment && environment !== "all" ? environment : null;

  const fetchData = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const result = await fetchSource({
        period,
        customRange,
        governedBroadcastId,
        governedAudienceId,
        governedProgramId,
        governedCampaignId,
        governedState,
        observedGhlEnvironment,
        lpeEnvironment,
      });
      setData(result);
    } catch (err) {
      setError(err?.message || "Failed to load Email performance data");
    } finally {
      setLoading(false);
    }
  }, [
    period,
    customRange,
    governedBroadcastId,
    governedAudienceId,
    governedProgramId,
    governedCampaignId,
    governedState,
    observedGhlEnvironment,
    lpeEnvironment,
    enabled,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    loading,
    error,
    data,
    summary: data?.summary || {},
    funnel: data?.funnel || [],
    attributionCoverage: data?.attributionCoverage ?? null,
    refresh: fetchData,
  };
}

export default useEmailPerformanceData;
